import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  PaymentRequestButtonElement,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  Base,
  Button,
  FieldGroup,
  Input,
  Label,
  StripeInput,
  Text,
  Header,
  Fields,
  Checkbox,
} from '@pet-notify/ui'

import PaymentMethodIcons from './PaymentMethodIcons'
import style from './style.module.css'
import { useMutation } from '@tanstack/react-query'
import {
  Stripe,
  StripeCardNumberElement,
  PaymentRequest,
} from '@stripe/stripe-js'
import { useActivateOrder } from 'hooks/orders'
import ErrorMessage from 'components/ErrorMessage'
import type { ActivateInput } from 'services/orders'
import { usePlans } from 'hooks/plans'

function createStripeToken({
  stripe,
  zip,
  cardNumberElement,
}: {
  stripe: Stripe
  zip: string
  cardNumberElement: StripeCardNumberElement
}) {
  return stripe
    // .createToken(cardNumberElement, {
    //   address_zip: zip,
    // })
    .createPaymentMethod({ type: 'card', card: cardNumberElement })
    .then(({ paymentMethod, error }) => {
      if (paymentMethod) {
        return paymentMethod
      } else if (error) {
        throw error.message
      } else {
        throw 'Something went wrong'
      }
    })
}

type FormData = {
  zip: string,
  policyAcceptance: boolean
  smsPermission: boolean
  saveCard: boolean
}


type Props = {
  state: Partial<ActivateInput>
  onSubmit: () => void
}

function PaymentForm({ state, ...props }: Props) {
  const stripe = useStripe()
  const elements = useElements()
  const { register, handleSubmit } = useForm<FormData>()
  const [paymentRequestKey, setPaymentRequestKey] = useState(0)
  const createTokenMutation = useMutation(createStripeToken)
  const activateMutation = useActivateOrder()
  const plans = usePlans()
  const paymentRequest = useRef<PaymentRequest>()
  useEffect(() => {
    if (stripe && plans.data) {
      const selectedPlan = plans.data.find(plan => plan.interval === state.planInterval);

      // console.log('SELECTED PLAN ::::',selectedPlan?.amount); // Get the ID of the yearly plan

      const newPaymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Pet Notify',
          amount: selectedPlan!.amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      newPaymentRequest.on('paymentmethod', async (ev: any) => {
        const { firstName, lastName, email, password, confirmPassword, policyAcceptance, smsPermission, activationCode, phone, planInterval } = state

        if (
          !stripe ||
          !elements ||
          !firstName ||
          !lastName ||
          !email ||
          !password ||
          !confirmPassword ||
          !policyAcceptance ||
          !smsPermission ||
          !phone ||
          !activationCode ||
          !planInterval
        ) {
          ev.complete('fail')
          return
        }

        activateMutation.mutate({
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
          policyAcceptance,
          smsPermission,
          activationCode,
          phone,
          planInterval,
          payment: { token: ev.paymentMethod.id },
          saveCard: false,
        }, {
          onSuccess: () => {
            ev.complete('success')
            props.onSubmit()
          },
        },
        )
      })

      // newPaymentRequest.on('paymentmethod', async ({ complete, token }) => {
      //   const {
      //     firstName,
      //     lastName,
      //     email,
      //     password,
      //     confirmPassword,
      //     policyAcceptance,
      //     smsPermission,
      //     activationCode,
      //     phone,
      //     planInterval,
      //   } = state

      //   if (
      //     !stripe ||
      //     !elements ||
      //     !firstName ||
      //     !lastName ||
      //     !email ||
      //     !password ||
      //     !confirmPassword ||
      //     !policyAcceptance ||
      //     !smsPermission ||
      //     !phone ||
      //     !activationCode ||
      //     !planInterval
      //   ) {
      //     return
      //   }

      //   activateMutation.mutate(
      //     {
      //       firstName,
      //       lastName,
      //       email,
      //       password,
      //       confirmPassword,
      //       policyAcceptance,
      //       smsPermission,
      //       activationCode,
      //       phone,
      //       planInterval,
      //       payment: { token: token.id },
      //       saveCard: false,
      //     },
      //     {
      //       onSuccess: () => {
      //         complete('success')
      //         props.onSubmit()
      //       },
      //     },
      //   )
      // })

      newPaymentRequest.canMakePayment().then((result) => {
        if (result) {
          paymentRequest.current = newPaymentRequest
          setPaymentRequestKey(prevKey => prevKey + 1)
        }
      })
    }
  }, [stripe, plans?.data, state.planInterval])

  const onSubmit = handleSubmit(({ zip, saveCard, policyAcceptance, smsPermission }) => {
    const {
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      activationCode,
      phone,
      planInterval,
    } = state

    if (
      !stripe ||
      !elements ||
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      !confirmPassword ||
      !policyAcceptance ||
      !smsPermission ||
      !phone ||
      !activationCode ||
      !planInterval
    ) {
      return
    }

    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement) {
      return
    }

    createTokenMutation.mutate(
      { stripe, zip, cardNumberElement },
      {
        onSuccess: (token) => {
          if (token) {
            activateMutation.mutate(
              {
                firstName,
                lastName,
                email,
                password,
                confirmPassword,
                policyAcceptance,
                smsPermission,
                activationCode,
                phone,
                planInterval,
                payment: { zip, token: token.id },
                saveCard: saveCard,
              },
              {
                onSuccess: () => {
                  props.onSubmit()
                },
              },
            )
          }
        },
      },
    )
  })

  return (
    <>
      <Header as='h3' mb='4' size='xlarge'>
        Payment information
      </Header>

      <Text mb='6'>No commitments, cancel anytime.</Text>

      <form onSubmit={onSubmit}>
        <Fields>
          <FieldGroup>
            <Label>
              Credit card number
              <StripeInput as={CardNumberElement} />
            </Label>
          </FieldGroup>

          <FieldGroup className={style.sideBySideFieldGroup}>
            <Label>
              Expiration (mm/yy)
              <StripeInput as={CardExpiryElement} />
            </Label>
            <Label>
              Security code (CVV)
              <StripeInput as={CardCvcElement} />
            </Label>
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor='zip'>ZIP code</Label>
            <Input
              className={style.zipInput}
              id='zip'
              {...register('zip', { required: true })}
            />
          </FieldGroup>

          <FieldGroup>
            <Checkbox
              label='Save card for future payments'
              id='saveCard'
              {...register('saveCard', { required: false, value: false })}
            />
          </FieldGroup>
          <Base mb='6'>

            {paymentRequest.current && (
              <div key={paymentRequestKey}>
                <PaymentRequestButtonElement options={{ paymentRequest: paymentRequest.current! }} />
              </div>
            )}
          </Base>

          <PaymentMethodIcons />
          <FieldGroup>
            {/* <Text className={style.bottomText}>Terms of Use: <a href="https://www.petnotify.com/terms-of-use">https://www.petnotify.com/terms-of-use</a></Text>  
            <Text className={style.bottomText}>Privacy Policy: <a href="https://www.petnotify.com/privacy-policy"> https://www.petnotify.com/privacy-policy</a></Text>  
            <Text className={style.bottomText}>By signing up I agree to Pet Notify's User Agreement and Privacy Policy</Text>   */}
            <div className={style.policyContainer}>
              <Checkbox
                label=''
                id='policyAcceptance'
                {...register('policyAcceptance', { required: true })}
              />
              <Text className={style.linkText}>
                By signing up I agree to Pet Notify's{' '}
                <a href="https://www.petnotify.com/terms-of-use" className={style.link}>
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href="https://www.petnotify.com/privacy-policy" className={style.link}>
                  Privacy Policy
                </a>
              </Text>
            </div>
          </FieldGroup>

          <FieldGroup>
            <div className={style.policyContainer}>
              <Checkbox
                label=""
                id='smsPermission'
                {...register('smsPermission', { required: true })}
              />
              <Text className={style.linkText}>
                By creating your Pet Notify account, you agree to receive SMS alerts to keep your pet safe. We’ll notify you immediately if your lost pet is found, needs care or there's an emergency. You can opt out at any time by replying STOP.
              </Text>
            </div>
          </FieldGroup>

          {activateMutation.isError && (
            <ErrorMessage error={activateMutation.error} />
          )}
        </Fields>


        <Button
          type='submit'
          disabled={createTokenMutation.isLoading || activateMutation.isLoading}
        >
          Confirm payment
        </Button>
      </form>
    </>
  )
}

export default PaymentForm
